import type { AuthResponse, UserProfile } from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status} ${detail}`);
  }
}

export interface Credentials {
  token: string;
  refreshToken: string;
}

export interface CredentialStore {
  load(): Credentials | null;
  save(credentials: Credentials): void;
  clear(): void;
}

/** Keeps the session across reloads. Storage can be unavailable (a private window), and
 * then the session simply lasts as long as the tab. */
export class LocalCredentialStore implements CredentialStore {
  private memory: Credentials | null = null;

  constructor(private readonly key = "radiola.session") {}

  load(): Credentials | null {
    try {
      const raw = localStorage.getItem(this.key);
      if (raw) return JSON.parse(raw) as Credentials;
    } catch {
      // Storage blocked or corrupt: fall back to the tab's memory.
    }
    return this.memory;
  }

  save(credentials: Credentials): void {
    this.memory = credentials;
    try {
      localStorage.setItem(this.key, JSON.stringify(credentials));
    } catch {
      // Kept in memory only.
    }
  }

  clear(): void {
    this.memory = null;
    try {
      localStorage.removeItem(this.key);
    } catch {
      // Nothing stored.
    }
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

/**
 * The backend's HTTP API as the phone's APIClient speaks it: a bearer access token that
 * lives an hour, and a rotating refresh token. A 401 triggers one refresh, shared by
 * every request that hit it, and one retry; a failed refresh ends the session.
 */
export class ApiClient {
  onSignedOut?: () => void;
  private refreshing: Promise<boolean> | null = null;

  constructor(
    private readonly store: CredentialStore,
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
    private readonly baseURL = "",
  ) {}

  get isSignedIn(): boolean {
    return this.store.load() !== null;
  }

  async signInWithKey(key: string): Promise<UserProfile> {
    const response = await this.request<AuthResponse>("/api/auth/access-key", {
      method: "POST",
      body: { key },
      auth: false,
    });
    this.adopt(response);
    return response.user;
  }

  async signOut(): Promise<void> {
    const credentials = this.store.load();
    this.store.clear();
    if (credentials) {
      await this.request("/api/auth/logout", {
        method: "POST",
        body: { refresh_token: credentials.refreshToken },
        auth: false,
      }).catch(() => undefined);
    }
    this.onSignedOut?.();
  }

  /** The current access token, for a request the client does not make itself. */
  accessToken(): string | null {
    return this.store.load()?.token ?? null;
  }

  async request<T = void>(path: string, options: RequestOptions = {}): Promise<T> {
    const auth = options.auth ?? true;
    let response = await this.send(path, options, auth);
    if (response.status === 401 && auth) {
      if (!(await this.refresh())) {
        this.store.clear();
        this.onSignedOut?.();
        throw new ApiError(401, "session_expired");
      }
      response = await this.send(path, options, auth);
    }
    if (!response.ok) {
      throw new ApiError(response.status, await errorDetail(response));
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private send(path: string, options: RequestOptions, auth: boolean): Promise<Response> {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers["Content-Type"] = "application/json";
    const token = auth ? this.store.load()?.token : undefined;
    if (token) headers.Authorization = `Bearer ${token}`;
    return this.fetchImpl(this.baseURL + path, {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  }

  private refresh(): Promise<boolean> {
    this.refreshing ??= this.performRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async performRefresh(): Promise<boolean> {
    const credentials = this.store.load();
    if (!credentials) return false;
    const response = await this.send(
      "/api/auth/refresh",
      { method: "POST", body: { refresh_token: credentials.refreshToken } },
      false,
    ).catch(() => null);
    if (!response?.ok) return false;
    this.adopt((await response.json()) as AuthResponse);
    return true;
  }

  private adopt(response: AuthResponse): void {
    if (!response.refresh_token) throw new ApiError(500, "no_refresh_token");
    this.store.save({ token: response.token, refreshToken: response.refresh_token });
  }
}

async function errorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    return typeof body.detail === "string" ? body.detail : response.statusText;
  } catch {
    return response.statusText;
  }
}
